import { BrowserModule } from "@angular/platform-browser";
import {
    NgModule,
    CUSTOM_ELEMENTS_SCHEMA,
    APP_INITIALIZER,
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgbModule, NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { Routes, RouterModule } from "@angular/router";
import { MatTooltipModule } from "@angular/material/tooltip";

// Calsium Modules
import { CommonModule } from "./common";
import { NotificationsModule } from "./notifications";
import { DashboardModule, dashboardRoutes } from "./dashboard";
import { FlowModule, flowRoutes } from "./flow";
import { MaterialModule, materialRoutes } from "./material";
import { AllocationModule, allocationRoutes } from "./allocation";
//import { AuthenticationModule, authRoutes } from './authentication';
import {
    ProductionPriceListModule,
    productionPriceListRoutes,
} from "./production-price-list";
import { reportsRoutes, ReportsModule } from "./reports";
import {
    pvaManualAdjustmentRoutes,
    PvaManualAdjustmentModule,
} from "./pva-manual-adjustment";
// shared modules in root registry
import { StateModule } from "../state";

import { ErrorHandlerService } from "./errorhandler/error-handler.service";

// Components
import { AppComponent } from "./app.component";
import { ToastrModule } from "ngx-toastr";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HighLightEditableCell } from "./shared/directives/highlighteditablecell.directive";
import { CalsiumDateFormatterPipe } from "./shared/calsium-date.pipe";
import { CalsiumDateFormatter } from "./shared/calsium-date-formatter";
import { ToastrHelperService } from "./shared/toastr-helper.service";
import { adminRoutes, AdminModule } from "./administration";
import { AddMarkupConditionMaterialComponent } from "./administration/pages/markup-condition-material/add-markup-condition-material/add-markup-condition-material.component";
import { MarkupConditionMaterialListComponent } from "./administration/pages/markup-condition-material/markup-condition-material-list/markup-condition-material-list.component";
import {
    BrowserCacheLocation,
    InteractionType,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication,
} from "@azure/msal-browser";
import { environment } from "src/environments/environment";
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalRedirectComponent,
    MsalService,
} from "@azure/msal-angular";
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from "@angular/common/http";
import { AuthService } from "./shared/auth.service";
import { AppMsalGuard, AppMsalInterceptor } from "./shared/app-auth-guards";
import { LoginComponent } from "./authentication/login/login.component";
import { TegelModule } from "@scania/tegel-angular-17";

export function waitForUserFactory(authService: AuthService) {
    return () => authService.userInfo.subscribe();
}

export function loggerCallback(logLevel: LogLevel, message: string) {
    console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.msalConfig.auth.clientId,
            authority: environment.msalConfig.auth.authority,
            redirectUri: "/",
            postLogoutRedirectUri: "/",
        },
        cache: {
            cacheLocation: BrowserCacheLocation.SessionStorage,
        },
        system: {
            allowPlatformBroker: false, // Disables WAM Broker
            loggerOptions: {
                loggerCallback,
                logLevel: LogLevel.Info,
                piiLoggingEnabled: false,
            },
        },
    });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set(
        environment.protectedResources.api.endpoint,
        environment.protectedResources.api.scopes
    );
    // protectedResourceMap.set(
    //   environment.protectedResources.api.endpoint,
    //   environment.protectedResources.api.scopes
    // );

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...environment.protectedResources.api.scopes],
        },
        loginFailedRoute: "/login-failed",
    };
}

// ============================================================================
const routes: Routes = [
    { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    { path: 'login', component: LoginComponent },
    // ...authRoutes,
    ...dashboardRoutes,
    ...flowRoutes,
    ...materialRoutes,
    ...productionPriceListRoutes,
    ...allocationRoutes,
    ...reportsRoutes,
    ...pvaManualAdjustmentRoutes,
    ...adminRoutes,
];
@NgModule({
    declarations: [AppComponent, HighLightEditableCell],
    imports: [
        ToastrModule.forRoot(),
        TegelModule,
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        NgbModule,
        MatTooltipModule,
        ReactiveFormsModule,
        RouterModule.forRoot(routes, {}),
        CommonModule,
        //  AuthenticationModule,
        DashboardModule,
        FlowModule,
        AllocationModule,
        StateModule,
        MaterialModule,
        ProductionPriceListModule,
        ReportsModule,
        PvaManualAdjustmentModule,
        AdminModule,
        NotificationsModule,
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: waitForUserFactory,
            deps: [AuthService],
            multi: true,
        },
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        { provide: HTTP_INTERCEPTORS, useClass: AppMsalInterceptor, multi: true },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        MsalService,
        { provide: MsalGuard, useClass: AppMsalGuard },
        MsalBroadcastService,
        ToastrHelperService,
        ErrorHandlerService,
        CalsiumDateFormatterPipe,
        { provide: NgbDateParserFormatter, useClass: CalsiumDateFormatter },
        AuthService,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
})
export class AppModule {}
